import { CornerDownLeft, Search, Terminal, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { parseWorkspaceSearch } from './workspace-search.ts';

type SearchRoute = 'tasks' | 'focus' | 'agents' | 'okrs';

export interface SearchCommand {
  id: string;
  label: string;
  aliases: string[];
  run(): void;
}

export interface TopSearchBarProps {
  route: SearchRoute;
  value: string;
  resultActive: boolean;
  commands: SearchCommand[];
  trailingActions: ReactNode;
  onChange(value: string): void;
  onSearch(value: string): void;
  onClear(): void;
}

export function TopSearchBar({
  route,
  value,
  resultActive,
  commands,
  trailingActions,
  onChange,
  onSearch,
  onClear,
}: TopSearchBarProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const parsed = parseWorkspaceSearch(value);
  const commandMode = parsed.mode === 'command';
  const commandQuery = parsed.commandQuery ?? '';
  const visibleCommands = useMemo(
    () =>
      commands.filter((command) =>
        [command.label, ...command.aliases].some((candidate) =>
          candidate.toLowerCase().includes(commandQuery),
        ),
      ),
    [commandQuery, commands],
  );

  useEffect(() => {
    let slashPending = false;
    let timer: number | undefined;
    const handler = (event: globalThis.KeyboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      if (event.key !== '/') return;
      event.preventDefault();
      if (slashPending) {
        onChange('/');
        inputRef.current?.focus();
        slashPending = false;
        if (timer !== undefined) window.clearTimeout(timer);
        return;
      }
      slashPending = true;
      inputRef.current?.focus();
      timer = window.setTimeout(() => {
        slashPending = false;
      }, 700);
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [onChange]);

  const runCommand = (command: SearchCommand): void => {
    command.run();
    onChange('');
    inputRef.current?.blur();
  };

  const submit = (): void => {
    if (commandMode) {
      const command = visibleCommands[0];
      if (command) runCommand(command);
      return;
    }
    onSearch(value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClear();
      inputRef.current?.blur();
    }
  };

  const placeholder =
    route === 'okrs'
      ? 'Search OKRs, KRs, linked tasks, or type /'
      : route === 'agents'
        ? 'Search commands or type /'
        : 'Search tasks, dates, or type /';

  return (
    <div className="flex w-full items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <div className="workspace-search">
          {commandMode ? (
            <Terminal
              aria-hidden="true"
              size={17}
              className="shrink-0 text-[color:var(--app-info)]"
            />
          ) : (
            <Search
              aria-hidden="true"
              size={17}
              className="shrink-0 text-[color:var(--app-text-soft)]"
            />
          )}
          <label htmlFor="workspace-top-search" className="sr-only">
            Workspace search
          </label>
          <input
            ref={inputRef}
            id="workspace-top-search"
            data-testid="workspace-top-search"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => window.setTimeout(() => setFocused(false), 120)}
            placeholder={placeholder}
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-text-soft)]"
          />
          {resultActive && (
            <span className="ui-chip ui-chip-info hidden shrink-0 px-2 py-0.5 text-[11px] md:inline-flex">
              Results
            </span>
          )}
          <button
            type="button"
            aria-label="Clear search"
            title="Clear search"
            onClick={onClear}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-[color:var(--app-text-soft)] transition hover:bg-white/70 hover:text-[color:var(--app-text)] ${
              value || resultActive ? '' : 'invisible'
            }`}
          >
            <X aria-hidden="true" size={15} />
          </button>
        </div>
        {commandMode && focused && (
          <div
            data-testid="workspace-command-menu"
            className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-[1100] max-h-80 overflow-y-auto rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-1 shadow-lg"
          >
            {visibleCommands.length === 0 ? (
              <p className="px-3 py-2 text-sm text-[color:var(--app-text-muted)]">
                No matching commands.
              </p>
            ) : (
              visibleCommands.map((command) => (
                <button
                  key={command.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runCommand(command)}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-[color:var(--app-text)] transition hover:bg-[color:var(--app-accent-soft)]"
                >
                  <span>{command.label}</span>
                  <CornerDownLeft
                    aria-hidden="true"
                    size={14}
                    className="text-[color:var(--app-text-soft)]"
                  />
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {trailingActions}
    </div>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}
